/**
 * ClientProgressBar — Tier 6B.2 booking-flow completion indicator
 *
 * Renders a 4-step horizontal rail (Proposal → Contract → Payment →
 * Planning) that tells the client exactly how far they are from locking
 * in their date. Lives at the top of /client-view/:token.
 *
 * Design goals
 *   - Zero backend change. Zero API change. Zero ClientViewPage regression.
 *   - Self-contained: fetches payment completion itself from the existing
 *     /api/payments/event/:eventId endpoint. Gracefully falls back to
 *     "unpaid" when eventId is missing or the fetch fails.
 *   - Mobile-first, no layout shift (fixed rail height, skeleton loading).
 *
 * State model
 *   - "completed"  — gold gradient node + check icon
 *   - "current"    — bright gold ring, slightly enlarged, pulses attention
 *   - "locked"     — muted gray node with number
 *
 * Planning is treated as OPTIONAL when the tenant has NOT triggered the
 * planning flow (data.planningTriggered === false). In that case the step
 * is pre-satisfied and labeled "Optional" underneath so clients don't
 * feel blocked by a step that's genuinely not required for them.
 */
import React, { useEffect, useState } from 'react';
import { paymentStatusFrom } from '../../lib/paymentStatus';
import { Check } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { key: 'proposal', label: 'Proposal' },
  { key: 'contract', label: 'Contract' },
  { key: 'payment',  label: 'Payment'  },
  { key: 'planning', label: 'Planning' },
];

const NEXT_STEP_COPY = {
  proposal: 'Next step: Review & approve your proposal',
  contract: 'Next step: Sign your contract to move forward',
  payment:  'Next step: Pay your retainer to lock in your date',
  planning: 'Next step: Share your planning details',
};

// Used only in the headline, so a client who has PAID is never told they are
// "1 step away from locking in your date" — the remaining thing gets named.
const REMAINING_STEP_NAME = {
  proposal: 'approving your proposal',
  contract: 'signing your contract',
};


const ClientProgressBar = ({
  proposalApproved,
  contractApproved,
  planningRequired,
  planningDone,
  eventId,
  // Portal access token — used by the token-gated payments read on the
  // client portal. The admin endpoint that this component used to call
  // is now tenant-locked, so the portal reads through the token-based
  // route instead. Admin-side renders can leave this undefined and
  // pass `paymentCompleteOverride` instead.
  portalToken,
  // Module keys the admin skipped in the pipeline. Those steps are removed
  // from the rail entirely - a client should never see a stage that was
  // never going to happen for their event.
  skippedSteps = [],
  // Allow tests / future callers to inject payment state directly and
  // bypass the internal fetch.
  paymentCompleteOverride = null,
  nextStepKeyOverride = undefined,
}) => {
  const [paymentComplete, setPaymentComplete] = useState(false);
  // What is ACTUALLY outstanding, from the same helper the payments tab uses.
  // Read even when `paymentCompleteOverride` decides the tick, because the
  // LOCK-IN question ("is the date secured?") and the TICK question ("is
  // every last dollar in?") are different questions. Conflating them told a
  // client whose retainer and contract balance were both settled — with only
  // a later $1.00 extra charge open — that they were "2 steps away from
  // locking in your date" and that their next step was to "Pay your
  // retainer".
  const [moneyStatus, setMoneyStatus] = useState(null);

  useEffect(() => {
    if (paymentCompleteOverride !== null) {
      setPaymentComplete(Boolean(paymentCompleteOverride));
    }
    if (!portalToken) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${API}/api/leads/client-view/${encodeURIComponent(portalToken)}/payments`
        );
        if (!res.ok) return;
        const d = await res.json();
        const list = Array.isArray(d.payments) ? d.payments : [];
        if (list.length === 0) return;
        const st = paymentStatusFrom(list, {
          totalPaid: Number(d.totalPaid) || 0,
          totalDue: Number(d.totalDue) || 0,
          isFullPayment: Boolean(d.isFullPayment),
        });
        if (cancelled) return;
        setMoneyStatus(st);
        // Feb-2026 fix (Andrea's bug): read CUMULATIVE dollars against the
        // deposit target — never a single line's status. A card payment
        // that happened to land on the Final Balance line while the
        // Retainer stayed pending must still lock the date, because the
        // deposit amount HAS been paid in aggregate.
        if (paymentCompleteOverride === null) {
          setPaymentComplete(st.state === 'paid' || st.retainerSettled);
        }
      } catch {
        /* graceful degrade — step stays "locked" */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [portalToken, paymentCompleteOverride]);

  // The booking "locks in" once proposal, contract, and the retainer payment
  // are done. Planning comes AFTER and is optional, so it never blocks the
  // lock-in count and is only marked done when actually submitted (no
  // premature checkmark).
  const planningOptional = !planningRequired;
  // NOT a skip flag. This means "planning is not REQUIRED for this event", and
  // it drives the "Optional" label under the planning node. It was named
  // `planningSkipped`, which collided with the real skip concept (see
  // isModuleSkipped in ClientViewPage) — a module the admin actually skipped
  // is removed from `visibleSteps` entirely and never reaches this label.
  const planningNotRequired = planningOptional && !planningDone;

  const stepStatus = {
    proposal: Boolean(proposalApproved),
    contract: Boolean(contractApproved),
    payment: Boolean(paymentComplete),
    planning: Boolean(planningDone),
  };

  const skipped = Array.isArray(skippedSteps) ? skippedSteps : [];
  const visibleSteps = STEPS.filter((s) => !skipped.includes(s.key));
  const LOCK_IN_KEYS = ['proposal', 'contract', 'payment'].filter((k) => !skipped.includes(k));
  // An extra charge added after the contract does not hold the date hostage.
  // The payment step counts as satisfied FOR LOCK-IN once the contract itself
  // is settled; the tick above still waits for every dollar.
  const lockInSatisfied = (k) => (
    k === 'payment' && moneyStatus
      ? (stepStatus.payment || moneyStatus.state === 'extras_only')
      : stepStatus[k]
  );
  const lockInDone = LOCK_IN_KEYS.filter(lockInSatisfied).length;
  const lockedIn = LOCK_IN_KEYS.length > 0 && lockInDone === LOCK_IN_KEYS.length;
  const stepsRemaining = LOCK_IN_KEYS.length - lockInDone;

  // Current step = first incomplete VISIBLE step; never point at an
  // optional planning step (the date is already locked by then).
  const firstIncompleteIdx = visibleSteps.findIndex((s) => !stepStatus[s.key]);
  let currentKey = firstIncompleteIdx === -1 ? null : visibleSteps[firstIncompleteIdx].key;
  if (currentKey === 'planning' && planningOptional) currentKey = null;

  // Filled line walks to the furthest completed node.
  const completedCount = visibleSteps.filter((s) => stepStatus[s.key]).length;
  const fillRatio =
    visibleSteps.length > 1
      ? Math.min(1, completedCount / (visibleSteps.length - 1))
      : 0;
  const fillWidthPct = 75 * fillRatio; // 75% = distance from 12.5% to 87.5%

  let headline;
  if (lockedIn) {
    headline = (planningRequired && !planningDone)
      ? "Your date is locked in — let's plan the details"
      : "You're all set — your date is locked in.";
  } else if (moneyStatus?.retainerSettled && !stepStatus.payment) {
    // The retainer secured the date. Saying "you're 2 steps away from locking
    // in your date" on the same screen as "your date is secured" made one of
    // the two a lie — and it was this one.
    const others = LOCK_IN_KEYS.filter((k) => k !== 'payment' && !stepStatus[k]);
    const extrasOnly = moneyStatus.state === 'extras_only';
    if (others.length === 0) {
      headline = extrasOnly
        ? 'Your date is secured and your contract is settled'
        : 'Your date is secured — your balance is the last step';
    } else if (others.length === 1 && REMAINING_STEP_NAME[others[0]]) {
      headline = extrasOnly
        ? `Your date is secured — ${REMAINING_STEP_NAME[others[0]]} is the last step`
        : `Your date is secured — ${REMAINING_STEP_NAME[others[0]]} and your balance remain`;
    } else {
      headline = 'Your date is secured — a few things still to finish';
    }
  } else if (stepsRemaining === 1) {
    // NEVER imply money when money is done. With the payment complete and one
    // step left, "You're 1 step away from locking in your date" read as a
    // payment prompt to a client who had paid in full. Name the actual thing.
    const lastKey = LOCK_IN_KEYS.find((k) => !lockInSatisfied(k));
    headline = lockInSatisfied('payment') && REMAINING_STEP_NAME[lastKey]
      ? `Your payment is complete — ${REMAINING_STEP_NAME[lastKey]} is the last step`
      : "You're 1 step away from locking in your date";
  } else {
    headline = `You're ${stepsRemaining} steps away from locking in your date`;
  }

  // The rail must never name a step the client cannot act on. Its own
  // `currentKey` only knows what was SKIPPED, not what was SENT — which
  // told a client with nothing sent to "sign your contract". When the
  // portal supplies an override, it wins; `null` means the ball is in
  // our court and no instruction is shown. Undefined = legacy callers.
  const copyKey =
    nextStepKeyOverride === undefined ? currentKey : nextStepKeyOverride;
  // NEVER name a step that is not actually outstanding. `NEXT_STEP_COPY.payment`
  // said "Pay your retainer to lock in your date" for ANY unpaid money, so a
  // client whose retainer was long settled was told to pay it again. The
  // wording for the payment step now comes from what is really open.
  const paymentCopy = moneyStatus?.nextStepCopy || NEXT_STEP_COPY.payment;
  const guidance =
    copyKey === 'done'
      ? null
      : copyKey
        ? (copyKey === 'payment' ? paymentCopy : NEXT_STEP_COPY[copyKey])
        : (nextStepKeyOverride === null
          ? "We'll email you as soon as your next step is ready."
          : null);

  return (
    <div
      className="rounded-2xl p-5 sm:p-6 mb-6"
      style={{
        background:
          'linear-gradient(180deg, rgba(255,255,255,0.035), rgba(255,255,255,0.015))',
        border: '1px solid rgba(212,175,55,0.14)',
        boxShadow:
          '0 4px 24px rgba(0,0,0,0.3), 0 0 0 1px rgba(212,175,55,0.03)',
      }}
      data-testid="client-progress-bar"
      role="region"
      aria-label="Booking progress"
    >
      {/* Micro-copy headline */}
      <p
        className="text-[12px] sm:text-[13px] text-[#D4AF37]/85 tracking-wide font-medium text-center mb-5"
        data-testid="client-progress-headline"
      >
        {headline}
      </p>

      {/* Rail */}
      <div className="relative" data-testid="client-progress-rail">
        {/* Background track */}
        <div
          aria-hidden
          className="absolute"
          style={{
            top: '18px',
            left: '12.5%',
            right: '12.5%',
            height: '2px',
            background: 'rgba(255,255,255,0.06)',
            borderRadius: '2px',
          }}
        />
        {/* Filled progress line */}
        <div
          aria-hidden
          className="absolute"
          style={{
            top: '18px',
            left: '12.5%',
            width: `${fillWidthPct}%`,
            height: '2px',
            background:
              'linear-gradient(90deg, #B88B1A 0%, #D4AF37 60%, #F2D989 100%)',
            boxShadow: '0 0 10px rgba(212,175,55,0.35)',
            borderRadius: '2px',
            transition: 'width 0.3s ease-out',
          }}
          data-testid="client-progress-line"
          data-fill-pct={String(Math.round(fillWidthPct))}
        />

        {/* Step nodes */}
        <div
          className="relative grid"
          style={{ gridTemplateColumns: `repeat(${Math.max(1, visibleSteps.length)}, minmax(0, 1fr))` }}
        >
          {visibleSteps.map((s, idx) => {
            const done = stepStatus[s.key];
            // Same key as the instruction line below (see `copyKey`, ~L156).
            // Highlighting `currentKey` here while the copy followed the
            // override made the rail contradict its own sentence. `copyKey`
            // may be null ("waiting on us") or 'done' — neither matches a
            // step key, so no circle highlights, which is correct.
            const current = s.key === copyKey;

            const sizeClass = current ? 'w-10 h-10' : 'w-9 h-9';

            let circleStyle = {
              background: 'rgba(255,255,255,0.04)',
              border: '1.5px solid rgba(255,255,255,0.1)',
              color: 'rgba(255,255,255,0.35)',
            };
            if (done) {
              circleStyle = {
                background:
                  'linear-gradient(180deg, #E6C86A 0%, #D4AF37 60%, #B88B1A 100%)',
                border: '1.5px solid rgba(212,175,55,0.9)',
                color: '#0B0F1A',
                boxShadow: '0 0 14px rgba(212,175,55,0.4)',
              };
            } else if (current) {
              circleStyle = {
                background: 'rgba(212,175,55,0.14)',
                border: '2px solid #D4AF37',
                color: '#F1D27A',
                boxShadow: '0 0 16px rgba(212,175,55,0.35)',
              };
            }

            const labelColor = done
              ? 'text-[#D4AF37]'
              : current
                ? 'text-[#F1D27A]'
                : 'text-white/35';

            const showOptional = s.key === 'planning' && planningNotRequired;

            return (
              <div
                key={s.key}
                className="flex flex-col items-center min-w-0"
                data-testid={`client-progress-step-${s.key}`}
                data-state={done ? 'completed' : current ? 'current' : 'locked'}
              >
                <div
                  className={`relative rounded-full flex items-center justify-center text-[12px] font-semibold transition-all duration-300 ${sizeClass}`}
                  style={circleStyle}
                  aria-label={`${s.label} — ${done ? 'completed' : current ? 'current step' : 'not yet started'}`}
                >
                  {/* A completed step KEEPS ITS NUMBER. It used to swap the
                      number for a tick, so a finished payment step read as
                      "1 Contract / Payment / 3 Planning" — the middle step
                      looked unnumbered and half-rendered rather than done.
                      Gold fill plus the tick badge says complete; the number
                      stays where the client expects it. */}
                  {idx + 1}
                  {done && (
                    <span
                      className="absolute -top-1 -right-1 rounded-full flex items-center justify-center"
                      style={{
                        width: 15, height: 15, background: '#0B0F1A',
                        border: '1.5px solid rgba(212,175,55,0.9)', color: '#F1D27A',
                      }}
                      data-testid={`client-progress-done-${s.key}`}
                      aria-hidden
                    >
                      <Check className="w-2.5 h-2.5" strokeWidth={3.5} />
                    </span>
                  )}
                </div>
                <span
                  className={`mt-2 text-[11px] sm:text-[12px] font-medium tracking-wide text-center ${labelColor}`}
                >
                  {s.label}
                  {showOptional && (
                    <span
                      className="block text-[9px] text-white/25 tracking-[0.15em] uppercase mt-0.5"
                      data-testid="client-progress-planning-optional"
                    >
                      Optional
                    </span>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Guidance line */}
      {guidance && (
        <p
          className="text-[11px] sm:text-[12px] text-white/50 text-center mt-5"
          data-testid="client-progress-guidance"
        >
          {guidance}
        </p>
      )}
    </div>
  );
};

export default ClientProgressBar;
export { ClientProgressBar, STEPS };
